/**
 * Employee Homepage Menu Component
 * 
 * Main menu page for employees after login, displaying available actions:
 * - Submit employment documents
 * - Edit employment documents
 * - Change name/address
 * - Upload identification documents
 * - Change password
 * 
 * Features:
 * - Language selection button
 * - List of action cards for different procedures
 * - Mobile-first design matching employee login styling
 * - Responsive: full screen on mobile, centered card with gradient on desktop
 */
import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import ActionCard from "./actionCard";
import PersonalInfoConsentBox from "../common/consentBox/PersonalInfoConsentBox";
import "./menu.css";
import Footer from "../common/footer/footer";
import submitIcon from "../../../assests/icons/submit_icon.png";
import editIcon from "../../../assests/icons/edit_icon.png";
import nameIcon from "../../../assests/icons/name_icon.png";
import idIcon from "../../../assests/icons/id-card.png";
import passwordIcon from "../../../assests/icons/pwd_icon.png";
import languageIcon from "../../../assests/icons/language_icon.png";  
import { getEmployeeEmail } from "../auth/employeeAuthStorage";
import { fetchChatFlow, fetchEmployeeAccountStatus } from "../../../services/chatFlowApi";
import { fetchEmployeeChatUnreadCount } from "../../../services/employeeChatApi";

export interface MenuProps {
  /** Called when user agrees to personal info consent from "Submit employment documents" - switch to checklist screen */
  onStartSubmitDocuments?: () => void;
  /** Called when user clicks "Edit employment documents" */
  onStartEditDocuments?: () => void;
  /** Called when user clicks メッセージ */
  onOpenMessages?: () => void;
  /** Called when user clicks パスワード変更 */
  onChangePassword?: () => void;
  /** Called when user clicks logout (upper left) */
  onLogout?: () => void;
}

const Menu: React.FC<MenuProps> = ({
  onStartSubmitDocuments,
  onStartEditDocuments,
  onOpenMessages,
  onChangePassword,
  onLogout,
}) => {
  const { t, language, setLanguage } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPersonalInfoConsent, setShowPersonalInfoConsent] = useState(false);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [resubmissionNeededCount, setResubmissionNeededCount] = useState<number>(0);
  const [chatUnreadCount, setChatUnreadCount] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const languageOptions: { code: 'ja' | 'en' | 'vi'; label: string }[] = [
    {code: 'ja', label: '日本語'},
    {code: 'en', label: 'English'},
    {code: 'vi', label: 'Tiếng Việt'},
  ];


  /**
   * Toggles the dropdown menu visibility
   */
  const handleLanguageButtonClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  /**
   * Handles language selection from dropdown
   * @param lang - Language to switch to ('ja' or 'en')
   */
  const handleLanguageSelect = (lang: 'ja' | 'en' | 'vi') => {
    setLanguage(lang);
    setIsDropdownOpen(false);
  };

  /**
   * Closes dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Load employee status + resubmission-needed badge count for the menu.
  useEffect(() => {
    const email = getEmployeeEmail();
    if (!email) return;
    let cancelled = false;

    fetchEmployeeAccountStatus(email).then((s) => {
      if (cancelled) return;
      setAccountStatus(s);
    });

    fetchChatFlow(email, language).then((flow) => {
      if (cancelled) return;
      const docs = flow?.documents ?? [];
      const forms = flow?.formsToFill ?? [];
      const sections = flow?.profileSections ?? [];
      const count =
        docs.filter((d) => d.adminApproved === false).length +
        forms.filter((f) => f.adminApproved === false).length +
        sections.filter((s) => s.adminApproved === false).length;
      setResubmissionNeededCount(count);
    });

    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    const email = getEmployeeEmail();
    if (!email) return;
    let cancelled = false;
    const tick = () => {
      fetchEmployeeChatUnreadCount(email).then((n) => {
        if (!cancelled) setChatUnreadCount(n);
      });
    };
    tick();
    const id = window.setInterval(tick, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const disableSubmitCard =
    accountStatus === 'submitted' ||
    accountStatus === 'resubmission_requested' ||
    accountStatus === 'resubmitted' ||
    accountStatus === 'cleared';

  /** Only while admin has requested resubmission (yellow dot on admin side). After employee submits, status is resubmitted → disabled until next request. */
  const disableEditCard = accountStatus !== 'resubmission_requested';

  return (
    <div className="menu-container">
      <div className={`menu-content${showPersonalInfoConsent ? ' menu-content--blurred' : ''}`}>
        {/* Top row: logout (left), language (right) */}
        <div className="menu-top-row">
          {onLogout && (
            <button type="button" className="menu-logout-btn" onClick={onLogout}>
              {t('menu.logout')}
            </button>
          )}
          <div className="language-dropdown" ref={dropdownRef}>
          <button className="language" onClick={handleLanguageButtonClick}>
            <img src={languageIcon} alt="" className="language-icon" aria-hidden />
            {t('menu.language')}
          </button>
          
          {/* Dropdown menu - list of language options */}
          {isDropdownOpen && (
            <ul className="language-dropdown-menu" role="listbox" aria-label="Select language">
              {languageOptions.map((opt) => (
                <li key={opt.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={language === opt.code}
                    className="language-dropdown-item"
                    onClick={() => handleLanguageSelect(opt.code)}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
          </div>
        </div>

        {/* Page title - translated based on selected language */}
        <h1 className="menu-title">{t('menu.title')}</h1>

        {/* List of action cards for different employee procedures */}
        <div className="action-list">
          {/* Submit employment documents - shows personal info consent */}
          <ActionCard
            label={t('menu.submitDocuments')}
            iconSrc={submitIcon}
            disabled={disableSubmitCard}
            onClick={() => {
              if (disableSubmitCard) return;
              setShowPersonalInfoConsent(true);
            }}
          />

          {/* Edit employment documents */}
          <ActionCard
            label={t('menu.editDocuments')}
            iconSrc={editIcon}
            disabled={disableEditCard}
            badgeCount={disableEditCard ? undefined : resubmissionNeededCount}
            onClick={() => {
              if (disableEditCard) return;
              onStartEditDocuments?.();
            }}
          />

          {/* Change name and address */}
          <ActionCard
            label={t('menu.changeName')}
            iconSrc={nameIcon}
            onClick={() => {}}
          />

          {/* Upload various identification documents */}
          <ActionCard
            label={t('menu.uploadId')}
            iconSrc={idIcon}
            onClick={() => {}}
          />

          {/* Change password */}
          <ActionCard
            label={t('menu.changePassword')}
            iconSrc={passwordIcon}
            onClick={() => onChangePassword?.()}
          />

          <ActionCard
            label={t('menu.messages')}
            iconSrc={editIcon}
            badgeCount={chatUnreadCount}
            badgeVariant="dot"
            onClick={() => onOpenMessages?.()}
          />
        </div>

        {/* Contact info - translated, newlines preserved */}
        <p className="menu-contact-info">{t('submitDocuments.contactInfo')}</p>

        {/* Footer component - displays copyright information */}
        <Footer layout="horizontal" />
      </div>

      {/* Personal information consent - shown in selected language */}
      {showPersonalInfoConsent && (
        <PersonalInfoConsentBox
          linkUrl="/personal-info"
          onAgree={() => {
            setShowPersonalInfoConsent(false);
            onStartSubmitDocuments?.();
          }}
          onDisagree={() => setShowPersonalInfoConsent(false)}
        />
      )}
    </div>
  );
};

export default Menu;
